import {ActivatedRoute, Router} from '@angular/router';
import {AfterViewInit, Component, ElementRef, OnDestroy, OnInit, Renderer2, ViewChild,} from '@angular/core';
import {AnalyticsHttpService} from '@api/http/analytics/analytics-http.service';
import {AnalyticsViewType} from '@api/models/analytics/analytics-view-type';
import {ConsentService} from '@utils/services/shared-consent/consent.service';
import {CurrentGamesComponent} from '@shared/modules/shared-games/components/current-games/current-games.component';
import {LayoutService} from '@utils/services/layout/layout.service';
import {HomeRoutes} from '@app/routes/home.routes';
import {INewsPost} from '@api/models/news/news';
import {ISpecial} from '@api/models/specials/specials';
import {ImageGalleryComponent} from '@components/image/image-gallery/image-gallery.component';
import {NewsDetailService} from '@shared/modules/shared-news-detail/services/news-detail.service';
import {StateService} from '@core/services/state.service';
import {Subscription} from 'rxjs';
import {UrlService} from '@utils/services/url/url.service';
import {TaboolaService} from '@shared/modules/shared-news-detail/services/taboola.service';
import {AnalyticsService} from '@utils/services/analytics/analytics.service';
import {AdService} from '@lib-modules/ads/services/ad.service';
import {Page} from '@lib/pages/page';
import {AdCampaignService} from '@components/ad-campaign/services/ad-campaign.service';
import {Tipp3ScriptsService} from '../../services/tipp3-scripts.service';
import {EntityType} from '@lib/models/entity/entity-type';
import { NewsSectionLayout } from '@lib-modules/section/components/news-section/news-section.component';
import {Entity} from '@lib/models/entity/entity';
import {SubscribeComponent} from '@modules/subscription/pages/subscribe/subscribe.component';
import {VideoPaywallOverlayComponent} from '@components/video-player/components/video-paywall-overlay/video-paywall-overlay.component';
import {VideoPlayerComponent} from '@components/video-player/components/video-player/video-player.component';

@Component({
  selector: 'news-detail',
  templateUrl: './news-detail.component.html',
  styleUrls: ['./news-detail.component.scss'],
})
export class NewsDetailComponent extends Page implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('gallery') public gallery: ImageGalleryComponent;
  @ViewChild(CurrentGamesComponent) public currentGames: CurrentGamesComponent;
  public special: ISpecial = {
    title: 'Elf der Runde',
    text: 'Aktuelle Votings & Ergebnisse',
    button: {
      text: 'Alle Infos',
      link: `${UrlService.Web}/${HomeRoutes.teamOfTheRound.index}`,
    },
  };
  public hasSeenLeagueNews = false;
  public showAds = true;
  public newsRowLayout = NewsSectionLayout.Row;
  public consent = false;
  private setupTaboola = true;
  private setupOddsServe = true;
  private taboolaScriptContainer: HTMLElement;
  private newsTextContainer: HTMLElement;
  private consent$: Subscription;
  private refresh$: Subscription;

  @ViewChild('taboolaContainer') private set taboolaContainer(container: ElementRef) {
    if (!container) {
      return;
    }
    this.taboolaScriptContainer = container.nativeElement;
    this.setupTaboolaScript();
  }

  constructor (
    public newsDetailService: NewsDetailService,
    private taboolaService: TaboolaService,
    private adsService: AdService,
    private adCampaign: AdCampaignService,
    private router: Router,
    private renderer: Renderer2,
    private activatedRoute: ActivatedRoute,
    private layoutService: LayoutService,
    private analyticsHttpService: AnalyticsHttpService,
    private consentService: ConsentService,
    private analyticsService: AnalyticsService,
    private tipp3ScriptsService: Tipp3ScriptsService,
  ) {
    super();
  }

  public ngOnInit() {
    // Prevent angular from reusing this route else routing
    // to the same route with different params is not working
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.newsDetailService.getTopNews();
    const news: INewsPost = this.activatedRoute.snapshot.data['newsEntry'];
    if (news.google_ad_targeting) {
      this.adCampaign.adTarget = news.google_ad_targeting;
    }
    this.newsDetailService.init(news);
    if (news?.sponsored) {
      this.showAds = false;
      this.adsService.showSidebarAds(false);
    }
    if (StateService.isBrowser) {
      this.analyticsHttpService.patchAddView(news._id, AnalyticsViewType.News);
    }
    this.setEntityForAnalytics(news);
    this.setPromoCode(news);
  }

  public async ngAfterViewInit(): Promise<void> {
    this.consent$ = this.consentService.consentChange.subscribe(() => {
      this.consent = this.consentService.hasAccepted();
      this.setupTaboolaScript();
      // this.injectTipp3Widget();
    });
  }

  public ngOnDestroy(): void {
    this.consent$?.unsubscribe();
    this.refresh$?.unsubscribe();
    this.adsService.showSidebarAds();
    this.taboolaScriptContainer = null;
    this.setupTaboola = false;
    this.adCampaign.reset();
    AnalyticsService.currentEntity = null;
  }

  public refresh() {
    this.analyticsHttpService.patchAddView(this.newsDetailService.newsEntry._id, AnalyticsViewType.News);
    this.newsDetailService.init(this.newsDetailService.newsEntry);
  }

  public onGalleryOpen(index: number): void {
    this.gallery.open(index);
    this.layoutService.headerInBackground = true;
    this.layoutService.setBodyScroll(false);
    this.adsService.refreshAll();
    this.analyticsService.sendPageView({ queryParams: new URLSearchParams([ [ 'view', 'image-gallery' ] ])});
  }

  public onGalleryClose(): void {
    if (!this.gallery.active) {
      return;
    }
    this.layoutService.headerInBackground = false;
    this.layoutService.setBodyScroll(true);
    this.adsService.refreshAll();
    this.analyticsService.sendPageView();
  }

  public onGalleryImageChange(): void {
    this.analyticsService.sendPageView({ queryParams: new URLSearchParams([ [ 'view', 'image-gallery' ] ])});
    this.adsService.refreshAll();
  }

  public leagueNewsInViewPort({ target, visible }): void {
    if (!StateService.isBrowser || !visible || this.hasSeenLeagueNews) {
      return;
    }
    this.hasSeenLeagueNews = true;
    this.newsDetailService.getLeagueNews();
  }

  public onTextRender(textElement: HTMLElement): void {
    this.newsTextContainer = textElement;
    // this.injectTipp3Widget();
  }

  private setupTaboolaScript(): void {

    if ( !StateService.isBrowser || !this.setupTaboola ||
         !this.consentService.hasAccepted() || !this.taboolaScriptContainer ) {
      return;
    }
    this.setupTaboola = false;
    setTimeout(() =>
      this.taboolaService.injectScripts(this.taboolaScriptContainer.parentElement, this.renderer),
    0);
  }

  // Tipp3 Quotenwidget
  private injectTipp3Widget(): void {
    const creator = this.newsDetailService.newsEntry.creator?.readable_id;
    if (creator !== 'kronen-zeitung') {
      return;
    }
    if (
      !StateService.isBrowser ||
      !this.consentService.hasAccepted() ||
      !this.setupOddsServe ||
      !this.newsTextContainer
    ) {
      return;
    }
    this.setupOddsServe = false;
    this.tipp3ScriptsService.inject(this.renderer, this.newsTextContainer);
  }

  private setEntityForAnalytics(news: INewsPost) {
    if (!news) {
      return;
    }
    AnalyticsService.currentEntity = new Entity({
      gender: news.sexes?.[0],
      id: news._id,
      title: news.title,
      type: EntityType.News,
      leagueName: news.ui_tags?.league_tags?.[0]?.ui_name,
      leagueId: news.ui_tags?.league_tags?.[0]?._id,
      stateName: news.ui_tags?.state_tags?.[0]?.ui_name,
      countryName: news.ui_tags?.country_tags?.[0]?.ui_name,
      paywall: news.videos_native?.[0]?.paywall
    })
  }

  private setPromoCode(news: INewsPost) {
    const promoCode = news?.streaming_info?.promo_code;
    if (!promoCode) {
      return;
    }
    VideoPlayerComponent.promoCode = promoCode;
  }
}
